import React, { useState, useEffect, useMemo } from 'react';
import { MovementType, PaymentMethod, UserRole, MovementCategory, UserType } from '../../types/index';
import { Plus, ArrowLeft, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { encontrarClientePorTelefone } from '../../shared/logica/telefone';
import { filtrarMovimentos } from './logicaMovimentos';
import { extractCountryCode } from '../../utils/phoneUtils';
import FiltrosMovimentos from './FiltrosMovimentos';
import TabelaMovimentos from './TabelaMovimentos';
import ModalDetalhesMovimento from './ModalDetalhesMovimento';
import ModalCriarMovimento from './ModalCriarMovimento';
import ConfirmModal from '../../components/ConfirmModal';
import ModalImprimirMovimentos from './ModalImprimirMovimentos';
import { traduzirMetodoPagamento, pagamentosDefaultMisto, formPagamentosFromMovimento, formatarResumoPagamentoMisto, movimentoUsaMetodo } from '../../shared/logica/pagamentos';

/**
 * Página de Movimentos (refatorada - dividida em componentes)
 */
export default function PaginaMovimentos({ company, movements, clients, currentUser, onAdd, onEdit, onDelete, forceShowModal, onCloseModal, onAddClient, users = [], onBack, techJobs = [], budgets = [] }) {
  const [showAdd, setShowAdd] = useState(forceShowModal || false);
  const [viewingMovement, setViewingMovement] = useState(null);
  const [editingMovement, setEditingMovement] = useState(null);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState(null);
  const [isMixedPayment, setIsMixedPayment] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState(null); // null, 'PENDING', ou 'DELIVERED'
  const [saleType, setSaleType] = useState('SIMPLE');
  const [createStep, setCreateStep] = useState('PICK_TYPE');
  const [valueReceived, setValueReceived] = useState('');
  const [matchedClientId, setMatchedClientId] = useState(null);
  const [docType, setDocType] = useState('SIMPLE_RECEIPT');
  const [countryCode, setCountryCode] = useState('351'); // Padrão: Portugal

  const [form, setForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    category: MovementCategory.OPERATIONAL,
    type: MovementType.VENDA,
    locationType: '', // 'FORA' ou 'NA_LOJA'
    client: '',
    phone: '',
    product: '', // Mantido para compatibilidade
    products: [], // Array de {name: string, ref: string}
    value: '',
    paymentMethod: PaymentMethod.CASH,
    paymentMethod2: PaymentMethod.CARD,
    payments: pagamentosDefaultMisto(),
    value1: '',
    value2: '',
    paymentDate1: '',
    paymentDate2: '',
    paymentTime1: '',
    paymentTime2: '',
    invoiceStatus: 'NAO_FATURADO',
    invoiceNumber: '',
    locality: '',
    street: '',
    doorNumber: '',
    zipCode: '',
    nif: '',
    email: '',
    notes: '',
    relatedTechJobId: '', // ID da assistência técnica vinculada
    relatedBudgetId: '', // ID do orçamento vinculado
    relatedType: '', // 'TECH' ou 'BUDGET'
    serviceCategory: '', // Categoria do serviço: AR_CONDICIONADO, PAINEL_SOLAR, OUTROS
    useDifferentBilling: false,
    billingClient: '',
    billingStreet: '',
    billingDoorNumber: '',
    billingLocality: '',
    billingZipCode: '',
    billingNif: ''
  });
  
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterMethods, setFilterMethods] = useState([]);
  const [filterInvoice, setFilterInvoice] = useState([]);
  const [filterDeliveryPending, setFilterDeliveryPending] = useState(false);
  const [filterDeliveryDelivered, setFilterDeliveryDelivered] = useState(false);
  const [filterUsers, setFilterUsers] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printFromDate, setPrintFromDate] = useState('');
  const [printToDate, setPrintToDate] = useState('');
  const [printMethods, setPrintMethods] = useState([]);

  useEffect(() => {
    if (forceShowModal) {
      setShowAdd(true);
      setCreateStep('PICK_TYPE');
    }
  }, [forceShowModal]);

  const closeAddModal = () => {
    setShowAdd(false);
    setCreateStep('PICK_TYPE');
    setMatchedClientId(null);
    setValueReceived('');
    setEditingMovement(null);
    setCountryCode('351'); // Reset para Portugal
    onCloseModal?.();
  };

  const openAddModal = () => {
    setEditingMovement(null);
    setSaleType('SIMPLE');
    setIsMixedPayment(false);
    setDeliveryStatus(null);
    setCreateStep('PICK_TYPE');
    setMatchedClientId(null);
    setValueReceived('');
    setCountryCode('351'); // Reset para Portugal
    setForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      category: MovementCategory.OPERATIONAL,
      type: MovementType.VENDA,
      locationType: '', // Reset
      client: '',
      phone: '',
      product: '',
      products: [{ name: '', ref: '' }], // Iniciar com um produto vazio
      value: '',
      paymentMethod: PaymentMethod.CASH,
      paymentMethod2: PaymentMethod.CARD,
      payments: pagamentosDefaultMisto(),
      value1: '',
      value2: '',
      paymentDate1: '',
      paymentDate2: '',
      paymentTime1: '',
      paymentTime2: '',
      invoiceStatus: 'NAO_FATURADO',
      invoiceNumber: '',
      locality: '',
      street: '',
      doorNumber: '',
      zipCode: '',
      nif: '',
      email: '',
      notes: '',
      relatedTechJobId: '',
      relatedBudgetId: '',
      relatedType: '',
      serviceCategory: '',
      useDifferentBilling: false,
      billingClient: '',
      billingStreet: '',
      billingDoorNumber: '',
      billingLocality: '',
      billingZipCode: '',
      billingNif: ''
    });
    setShowAdd(true);
  };

  const openEditModal = (movement) => {
    setEditingMovement(movement);
    
    // Determinar docType baseado no movimento
    const isInvoiceReceipt = movement.invoiceStatus !== 'NAO_FATURADO' || movement.invoiceNumber || movement.phone || movement.nif;
    setDocType(isInvoiceReceipt ? 'INVOICE_RECEIPT' : 'SIMPLE_RECEIPT');
    
    // Determinar saleType
    const movementSaleType = movement.saleType || (movement.client === 'Consumidor Final' ? 'SIMPLE' : 'CLIENT');
    setSaleType(movementSaleType);
    
    setIsMixedPayment(movement.isMixedPayment || false);
    // Compatibilidade: se tem hasDelivery true, considerar como PENDING; se tem deliveryStatus, usar ele
    setDeliveryStatus(movement.deliveryStatus || (movement.hasDelivery ? 'PENDING' : null));
    setCreateStep('FORM'); // Ir direto para o formulário
    setMatchedClientId(null);
    setValueReceived('');
    
    // Extrair código do país do telefone se existir
    let extractedCountryCode = '351'; // Padrão: Portugal
    let phoneNumber = movement.phone || '';
    if (movement.phone) {
      const extracted = extractCountryCode(movement.phone);
      extractedCountryCode = extracted.countryCode;
      phoneNumber = extracted.phoneNumber;
    }
    setCountryCode(extractedCountryCode);
    
    // Helper para extrair data e hora de um campo datetime
    const extractDateAndTime = (datetimeStr) => {
      if (!datetimeStr) return { date: '', time: '' };
      // Se contém T (formato ISO datetime), separar data e hora
      if (datetimeStr.includes('T')) {
        const [datePart, timePart] = datetimeStr.split('T');
        const time = timePart ? timePart.substring(0, 5) : ''; // HH:mm (primeiros 5 caracteres)
        return { date: datePart, time: time };
      }
      // Se for apenas data (yyyy-MM-dd), retornar data e hora vazia
      return { date: datetimeStr, time: '' };
    };

    const dateTime1 = extractDateAndTime(movement.paymentDate1);
    const dateTime2 = extractDateAndTime(movement.paymentDate2);

    // Preencher formulário com dados do movimento
    setForm({
      date: movement.date || format(new Date(), 'yyyy-MM-dd'),
      category: movement.category || MovementCategory.OPERATIONAL,
      type: movement.type || MovementType.VENDA,
      locationType: movement.locationType || '',
      client: movement.client || '',
      phone: phoneNumber, // Usar número sem código do país (o código está em countryCode)
      product: movement.product || '',
      products: movement.products || (movement.product ? [{ name: movement.product, ref: '' }] : []),
      value: movement.value?.toString() || '',
      paymentMethod: movement.paymentMethod || PaymentMethod.CASH,
      paymentMethod2: movement.paymentMethod2 || PaymentMethod.CARD,
      payments: formPagamentosFromMovimento(movement),
      value1: movement.value1?.toString() || '',
      value2: movement.value2?.toString() || '',
      paymentDate1: dateTime1.date,
      paymentDate2: dateTime2.date,
      paymentTime1: movement.paymentTime1 || dateTime1.time,
      paymentTime2: movement.paymentTime2 || dateTime2.time,
      invoiceStatus: movement.invoiceStatus || 'NAO_FATURADO',
      invoiceNumber: movement.invoiceNumber || '',
      locality: movement.locality || '',
      street: movement.street || '',
      doorNumber: movement.doorNumber || '',
      zipCode: movement.zipCode || '',
      nif: movement.nif || '',
      email: movement.email || '',
      notes: movement.notes || '',
      relatedTechJobId: movement.relatedTechJobId || '',
      relatedBudgetId: movement.relatedBudgetId || '',
      relatedType: movement.relatedType || (movement.relatedTechJobId ? 'TECH' : (movement.relatedBudgetId ? 'BUDGET' : '')),
      serviceCategory: movement.serviceCategory || '',
      useDifferentBilling: !!(movement.billingClient || movement.useDifferentBilling),
      billingClient: movement.billingClient || '',
      billingStreet: movement.billingStreet || '',
      billingDoorNumber: movement.billingDoorNumber || '',
      billingLocality: movement.billingLocality || '',
      billingZipCode: movement.billingZipCode || '',
      billingNif: movement.billingNif || ''
    });
    
    setShowAdd(true);
  };

  // Lookup inteligente: ao introduzir telefone, preencher dados do cliente automaticamente
  useEffect(() => {
    if (!showAdd) return;
    if (saleType !== 'CLIENT') {
      setMatchedClientId(null);
      return;
    }
    const match = encontrarClientePorTelefone(clients, form.phone, countryCode);

    if (!match) {
      setMatchedClientId(null);
      return;
    }

    setMatchedClientId(match.id);
    setForm(prev => ({
      ...prev,
      client: match.name || prev.client,
      locality: match.locality || prev.locality,
      street: match.street || prev.street,
      doorNumber: match.doorNumber || prev.doorNumber,
      zipCode: match.zipCode || prev.zipCode,
      nif: match.nif || prev.nif,
      email: match.email || prev.email
    }));
  }, [showAdd, saleType, form.phone, clients, countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Regras por tipo de documento (só aplicar quando não está em modo de edição)
  useEffect(() => {
    if (!showAdd || editingMovement) return;
    if (docType === 'SIMPLE_RECEIPT') {
      setSaleType('SIMPLE');
      setMatchedClientId(null);
      setForm(prev => ({
        ...prev,
        invoiceStatus: 'NAO_FATURADO',
        invoiceNumber: '',
        phone: '',
        client: '',
        street: '',
        doorNumber: '',
        locality: '',
        zipCode: '',
        nif: '',
        email: ''
      }));
    } else {
      // Quando é Fatura/Recibo, default é Cliente (não Consumidor Final)
      setSaleType('CLIENT');
      setForm(prev => ({
        ...prev,
        invoiceStatus: prev.invoiceStatus === 'NAO_FATURADO' ? 'PARA_FATURAR' : prev.invoiceStatus
      }));
    }
  }, [docType, showAdd, editingMovement]);

  // Botão Editar: visível para todos nesta página (Admin, Dono, Empregado complexo). PaginaMovimentos só é mostrada a utilizadores não-simples.
  const isEmployeeComplex = currentUser?.role === UserRole.EMPLOYEE && (currentUser?.userType === UserType.COMPLEX || !currentUser?.userType);
  const canEdit = true; // Todos os que veem esta página podem editar (restrições aplicadas no modal para empregado complexo)
  const canDelete = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.OWNER;

  const filtered = useMemo(() => {
    const list = filtrarMovimentos(movements, search, filterTypes, filterMethods, filterInvoice, filterDeliveryPending, filterDeliveryDelivered, filterUsers);
    // Normalizar: nunca passar invoiceNumber para a tabela quando status não é FATURADO (corrige dados inconsistentes na BD)
    return list.map(m => {
      const status = String(m.invoiceStatus || '').toUpperCase();
      if (status !== 'FATURADO' && m.invoiceNumber) {
        return { ...m, invoiceNumber: '' };
      }
      return m;
    });
  }, [movements, search, filterTypes, filterMethods, filterInvoice, filterDeliveryPending, filterDeliveryDelivered, filterUsers]);

  const printFiltered = useMemo(() => {
    return filtered.filter(m => {
      if (printFromDate && m.date < printFromDate) return false;
      if (printToDate && m.date > printToDate) return false;
      if (printMethods.length === 0) return true;
      return printMethods.some(method => movimentoUsaMetodo(m, method));
    });
  }, [filtered, printFromDate, printToDate, printMethods]);

  const printTotal = useMemo(() => {
    return printFiltered.reduce((acc, m) => acc + (Number(m.value) || 0), 0);
  }, [printFiltered]);

  const openPrintModal = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const firstDayOfMonth = format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd');
    setPrintFromDate(firstDayOfMonth);
    setPrintToDate(today);
    setPrintMethods([]);
    setShowPrintModal(true);
  };

  const togglePrintMethod = (method) => {
    setPrintMethods(prev => (prev.includes(method) ? prev.filter(x => x !== method) : [...prev, method]));
  };

  const escapeHtml = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };

  const handlePrint = () => {
    // Na impressão: ordem do mais antigo para o mais recente (inverso do ecrã)
    const rows = [...printFiltered].reverse();
    const companyName = company?.name ? escapeHtml(company.name) : 'Empresa';

    const periodLabel = `${printFromDate ? format(new Date(printFromDate), 'dd/MM/yyyy') : '—'} — ${
      printToDate ? format(new Date(printToDate), 'dd/MM/yyyy') : '—'
    }`;
    const methodsLabel =
      printMethods.length === 0 ? 'Todos' : printMethods.map(m => traduzirMetodoPagamento(m)).join(', ');

    const html = `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Movimentos - ${companyName}</title>
    <style>
      :root { --text:#0f172a; --muted:#64748b; --border:#e2e8f0; }
      * { box-sizing: border-box; }
      html, body { height: auto; }
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; color: var(--text); margin: 0; padding: 16px; }
      h1 { font-size: 16px; margin: 0 0 4px 0; }
      .sub { color: var(--muted); font-size: 11px; margin-bottom: 12px; }
      .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; margin-bottom: 12px; font-size: 11px; }
      .meta b { color: var(--text); }
      table { width: 100%; border-collapse: collapse; font-size: 11px; page-break-inside: auto; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      th, td { border: 1px solid var(--border); padding: 6px; vertical-align: top; }
      th { background: #f8fafc; text-align: left; font-weight: 700; }
      .right { text-align: right; }
      .small { color: var(--muted); font-size: 10px; line-height: 1.2; }
      td { word-break: break-word; }
      @media print {
        body { padding: 0; }
        @page { margin: 8mm; }
      }
    </style>
  </head>
  <body>
    <h1>Histórico de Movimentos — ${companyName}</h1>
    <div class="sub">Impressão gerada em ${escapeHtml(format(new Date(), 'dd/MM/yyyy HH:mm'))}</div>
    <div class="meta">
      <div><b>Período:</b> ${escapeHtml(periodLabel)}</div>
      <div><b>Método:</b> ${escapeHtml(methodsLabel)}</div>
      <div><b>Nº movimentos:</b> ${rows.length}</div>
      <div><b>Total:</b> ${(printTotal || 0).toFixed(2)}€</div>
    </div>
    <table>
      <thead>
        <tr>
          <th style="width: 90px;">Data</th>
          <th>Cliente / Detalhes</th>
          <th style="width: 120px;">Pagamento</th>
          <th style="width: 110px;">Faturação</th>
          <th class="right" style="width: 90px;">Valor</th>
        </tr>
      </thead>
      <tbody>
        ${
          rows
            .map(m => {
              const date = m.date ? format(new Date(m.date), 'dd/MM/yyyy') : '';
              const details =
                m.products && m.products.length > 0
                  ? m.products.map(p => `${p.name}${p.ref ? ` (Ref: ${p.ref})` : ''}`).join(', ')
                  : (m.product || '');
              const payment = m.isMixedPayment
                ? `Misto: ${formatarResumoPagamentoMisto(m)}`
                : traduzirMetodoPagamento(m.paymentMethod);
              const invoice = m.invoiceStatus ? `${m.invoiceStatus}${m.invoiceNumber && m.invoiceStatus === 'FATURADO' ? ` — Nº ${m.invoiceNumber}` : ''}` : '';
              const value = (Number(m.value) || 0).toFixed(2);
              return `
                <tr>
                  <td>${escapeHtml(date)}<div class="small">${escapeHtml(m.type || '')}</div></td>
                  <td><b>${escapeHtml(m.client || '')}</b><div class="small">${escapeHtml(details)}</div></td>
                  <td>${escapeHtml(payment)}${m.paymentTime1 ? `<div class="small">Hora: ${escapeHtml(m.paymentTime1)}</div>` : ''}</td>
                  <td>${escapeHtml(invoice)}</td>
                  <td class="right"><b>${escapeHtml(value)}€</b></td>
                </tr>
              `;
            })
            .join('')
        }
      </tbody>
    </table>
  </body>
</html>
    `;

    // NOTE: Some browsers will block printing if it's not executed synchronously
    // inside the user gesture handler. Avoid setTimeout before calling print().
    const w = window.open('', '_blank');
    if (!w) {
      window.alert('O navegador bloqueou a janela de impressão. Permita pop-ups para este site e tente novamente.');
      return;
    }
    try {
      // Security: avoid giving the new window access back to this one.
      w.opener = null;
    } catch {
      // ignore
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();

    // Try to print synchronously (required by some browsers on localhost/dev)
    try {
      w.print();
      w.onafterprint = () => w.close();
    } catch (err) {
      // Fallback: if the browser needs a render tick, retry once.
      console.error('Print failed, retrying...', err);
      setTimeout(() => {
        try {
          w.print();
          w.onafterprint = () => w.close();
        } catch (err2) {
          console.error('Print failed окончательно:', err2);
          window.alert('Não foi possível abrir a impressão automaticamente. Tente novamente ou verifique as permissões do navegador.');
        }
      }, 200);
    }
  };

  const handleDelete = (id) => {
    setMovementToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (movementToDelete) {
      onDelete(movementToDelete);
      setShowDeleteModal(false);
      setMovementToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMovementToDelete(null);
  };

  const handleSubmitMovement = (payload) => {
    if (editingMovement) {
      let finalPayload;
      if (isEmployeeComplex) {
        // Empregado complexo: pode alterar data, métodos de pagamento, faturação e acrescentar produtos
        const originalCount = editingMovement.products?.length ?? (editingMovement.product ? 1 : 0);
        const originalProducts = editingMovement.products ?? (editingMovement.product ? [{ name: editingMovement.product, ref: '' }] : []);
        const newProducts = (payload.products || []).slice(originalCount);
        const mergedProducts = [...originalProducts, ...newProducts];
        finalPayload = {
          ...editingMovement,
          date: payload.date ?? editingMovement.date,
          paymentMethod: payload.paymentMethod ?? editingMovement.paymentMethod,
          paymentDate1: payload.paymentDate1 ?? editingMovement.paymentDate1,
          paymentTime1: payload.paymentTime1 ?? editingMovement.paymentTime1,
          invoiceStatus: payload.invoiceStatus ?? editingMovement.invoiceStatus,
          invoiceNumber: payload.invoiceStatus === 'FATURADO' ? (payload.invoiceNumber ?? editingMovement.invoiceNumber) : '',
          products: mergedProducts.length > 0 ? mergedProducts : undefined,
          product: mergedProducts.length > 0 ? mergedProducts.map(p => p.ref ? `${p.name} (Ref: ${p.ref})` : p.name).join(', ') : editingMovement.product,
          createdBy: editingMovement.createdBy || currentUser.id
        };
        if (editingMovement.isMixedPayment) {
          finalPayload.paymentMethod2 = payload.paymentMethod2 ?? editingMovement.paymentMethod2;
          finalPayload.paymentDate2 = payload.paymentDate2 ?? editingMovement.paymentDate2;
          finalPayload.paymentTime2 = payload.paymentTime2 ?? editingMovement.paymentTime2;
          finalPayload.payments = payload.payments ?? editingMovement.payments;
          finalPayload.value1 = payload.value1 ?? editingMovement.value1;
          finalPayload.value2 = payload.value2 ?? editingMovement.value2;
        }
      } else {
        finalPayload = { ...editingMovement, ...payload, createdBy: editingMovement.createdBy || currentUser.id };
      }
      onEdit(finalPayload);
      setEditingMovement(null);
    } else {
      onAdd({ ...payload, createdBy: currentUser.id });
    }
  };

  const isSimpleUser = currentUser?.userType === UserType.SIMPLE;

  // Função para lidar com mudança de pagamento misto - preenche paymentDate1 com data atual quando ativado
  const handleMixedPaymentChange = (newValue) => {
    setIsMixedPayment(newValue);
    if (newValue) {
      setForm(prev => ({
        ...prev,
        payments: [
          {
            method: prev.paymentMethod,
            value: prev.value || '',
            paymentDate: prev.paymentDate1 || format(new Date(), 'yyyy-MM-dd'),
            paymentTime: prev.paymentTime1 || ''
          },
          {
            method: PaymentMethod.CARD,
            value: '',
            paymentDate: '',
            paymentTime: ''
          }
        ],
        paymentDate1: prev.paymentDate1 || format(new Date(), 'yyyy-MM-dd')
      }));
    } else {
      setForm(prev => ({
        ...prev,
        payments: pagamentosDefaultMisto(),
        paymentMethod2: PaymentMethod.CARD,
        value1: '',
        value2: '',
        paymentDate1: '',
        paymentDate2: '',
        paymentTime2: ''
      }));
    }
  };

  // Função para marcar movimento como entregue ou não entregue
  const handleToggleDeliveryStatus = (movement) => {
    if (!movement) return;
    
    const newDeliveryStatus = movement.deliveryStatus === 'DELIVERED' ? null : 'DELIVERED';
    const updates = {
      ...movement,
      deliveryStatus: newDeliveryStatus,
      hasDelivery: !!newDeliveryStatus, // Compatibilidade
      deliveredAt: newDeliveryStatus === 'DELIVERED' ? new Date().toISOString() : null
    };
    
    onEdit(updates);
    
    // Se estiver visualizando este movimento, atualizar também no estado
    if (viewingMovement && viewingMovement.id === movement.id) {
      setViewingMovement(updates);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {isSimpleUser && onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors bg-white shadow-sm border border-slate-200"
            >
              <ArrowLeft size={24} className="text-slate-600" />
            </button>
          )}
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Histórico de Movimentos</h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={openPrintModal}
            className="bg-white text-slate-700 px-6 py-4 rounded-2xl font-black flex items-center gap-2 hover:bg-slate-50 transition-all border border-slate-200 shadow-sm"
          >
            <Printer size={20} /> Imprimir
          </button>
          <button 
            onClick={openAddModal}
            className="bg-[#1b5e20] text-white px-8 py-4 rounded-2xl font-black flex items-center gap-2 hover:bg-green-900 transition-all transform active:scale-95 shadow-xl"
          >
            <Plus size={24} /> Novo Registo
          </button>
        </div>
      </div>

      <FiltrosMovimentos
        search={search}
        onSearchChange={setSearch}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filterTypes={filterTypes}
        filterMethods={filterMethods}
        filterInvoice={filterInvoice}
        filterDeliveryPending={filterDeliveryPending}
        filterDeliveryDelivered={filterDeliveryDelivered}
        filterUsers={filterUsers}
        onFilterTypesChange={setFilterTypes}
        onFilterMethodsChange={setFilterMethods}
        onFilterInvoiceChange={setFilterInvoice}
        onFilterDeliveryPendingChange={setFilterDeliveryPending}
        onFilterDeliveryDeliveredChange={setFilterDeliveryDelivered}
        onFilterUsersChange={setFilterUsers}
        onClearFilters={() => {
          setFilterTypes([]);
          setFilterMethods([]);
          setFilterInvoice([]);
          setFilterDeliveryPending(false);
          setFilterDeliveryDelivered(false);
          setFilterUsers([]);
        }}
        users={users.filter(u => u.companies.includes(company.id))}
      />

      <TabelaMovimentos
        movimentos={filtered}
        onViewMovement={setViewingMovement}
        onEditMovement={openEditModal}
        onDeleteMovement={handleDelete}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      <ModalImprimirMovimentos
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        fromDate={printFromDate}
        toDate={printToDate}
        onFromDateChange={setPrintFromDate}
        onToDateChange={setPrintToDate}
        selectedMethods={printMethods}
        onToggleMethod={togglePrintMethod}
        previewCount={printFiltered.length}
        previewTotal={printTotal}
        onPrint={handlePrint}
      />

      <ModalDetalhesMovimento
        movimento={viewingMovement}
        onClose={() => setViewingMovement(null)}
        onMarkAsDelivered={(movement) => handleToggleDeliveryStatus(movement)}
        techJobs={techJobs}
        budgets={budgets}
      />

      <ModalCriarMovimento
        isOpen={showAdd}
        onClose={closeAddModal}
        form={form}
        onFormChange={setForm}
        createStep={createStep}
        onCreateStepChange={setCreateStep}
        docType={docType}
        onDocTypeChange={setDocType}
        saleType={saleType}
        onSaleTypeChange={setSaleType}
        isMixedPayment={isMixedPayment}
        onIsMixedPaymentChange={handleMixedPaymentChange}
        deliveryStatus={deliveryStatus}
        onDeliveryStatusChange={setDeliveryStatus}
        valueReceived={valueReceived}
        onValueReceivedChange={setValueReceived}
        matchedClientId={matchedClientId}
        isEditing={!!editingMovement}
        onSubmit={handleSubmitMovement}
        currentUser={currentUser}
        countryCode={countryCode}
        onCountryCodeChange={setCountryCode}
        isSimpleUser={isSimpleUser}
        techJobs={techJobs}
        budgets={budgets}
        editingMovement={editingMovement}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title="Eliminar Movimento"
        message="Atenção: Esta ação é definitiva e afetará os relatórios financeiros. Deseja eliminar este movimento?"
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}
